import { destroy, invalidator, create } from '@dojo/framework/core/vdom';
import injector from '@dojo/framework/core/middleware/injector';
import Store, { StatePaths, Path } from '@dojo/framework/stores/Store';
import { Process } from '@dojo/framework/stores/process';
//
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { MiddlewareResultFactory, MiddlewareMap, WidgetProperties, DNode } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { RegistryHandler } from '@dojo/framework/core/RegistryHandler';

import { StoreApi, StoreMiddlewareResult } from './interfaces';

const factory = create({ destroy, invalidator, injector });

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const createStoreMiddleware = <S = any>(
	statename: string = 'state',
	initial?: (store: Store<S>) => void
): StoreMiddlewareResult<S> => {
	let store: Store<S>;
	let initialized = false;
	const storeMiddleware = factory(({ middleware: { destroy, invalidator, injector } }) => {
		const handles: any[] = [];
		destroy(() => {
			let handle: any;
			while ((handle = handles.pop())) {
				handle();
			}
		});
		if (!initialized) {
			const injectedStore = injector.get<Store<S>>(statename);
			if (injectedStore) {
				store = injectedStore;
			}
			initial && initial(store);
			initialized = true;
		}
		const registeredPaths: string[] = [];
		const path: StatePaths<S> = (path: any, ...segments: any) => {
			return (store as any).path(path, ...segments);
		};

		const api: Partial<StoreApi<S>> = {};
		api.getStore = (): Store<S> => store;

		api.setStore = (storeval: Store<S>) => {
			if (store && storeval !== store) {
				let handle: any;
				while ((handle = handles.pop())) {
					handle();
				}
			}
			store = storeval;
		};

		api.get = <U = any>(path: Path<S, U>, dontWatch: boolean = false): U => {
			if (!dontWatch && registeredPaths.indexOf(path.path) === -1) {
				const handle = store.onChange(path, () => {
					invalidator();
				});
				handles.push(() => handle.remove());
				registeredPaths.push(path.path);
			}
			return store.get(path);
		};

		api.path = path;

		api.at = <U = any>(path: Path<S, U[]>, index: number) => store.at(path, index);

		api.executor = <T extends Process<any, any>>(process: T): ReturnType<T> => process(store) as any;
		api.run = api.executor;

		return api as StoreApi<S>;
	});
	return storeMiddleware;
};
